import { createSlice } from "@reduxjs/toolkit";

import {
	ConversationImportResult,
	ImportedConversation,
	ImportedConversationHistory,
} from "@signalist/shared-types";

import { RootState } from "@/redux";

import { CcRecipientInfo } from "@/schemas/cc-recipient";

import { conversationApi } from "./conversation.api";

interface ConversationState {
	/** Resolved CC recipients, keyed by lowercased email address. */
	ccRecipients: Record<string, CcRecipientInfo>;
	/**
	 * The viewer's imported earlier conversation (PREC-4305), bucketed by the list person it was
	 * requested for: a cached lead is served without a new `fulfilled`, so one bucket would show
	 * the last fetched lead's history under another lead.
	 */
	importedByListPersonId: Record<number, ImportedConversationHistory>;
	/** The last "Load earlier conversation" answer, filed under the lead it was asked for. */
	importResultByListPersonId: Record<number, ConversationImportResult>;
}

const initialState: ConversationState = {
	ccRecipients: {},
	importedByListPersonId: {},
	importResultByListPersonId: {},
};

export const conversationSlice = createSlice({
	name: "conversation",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addMatcher(
			conversationApi.endpoints.resolveCcRecipients.matchFulfilled,
			(state, { payload }) => {
				for (const recipient of payload) {
					state.ccRecipients[recipient.email.toLowerCase()] = recipient;
				}
			}
		);
		builder.addMatcher(
			conversationApi.endpoints.getImportedConversations.matchFulfilled,
			(state, { payload, meta }) => {
				state.importedByListPersonId[meta.arg.originalArgs] = payload;
			}
		);
		builder.addMatcher(
			conversationApi.endpoints.importEarlierConversation.matchFulfilled,
			(state, { payload, meta }) => {
				state.importResultByListPersonId[meta.arg.originalArgs] = payload;
			}
		);
	},
});

export const conversationReducer = conversationSlice.reducer;

export const conversationSelector = (state: RootState) => state[conversationSlice.name];

const NO_IMPORTED: ImportedConversation[] = [];

/** The viewer's imported history with the lead behind this list person, oldest first. */
export const selectImportedConversations = (
	state: RootState,
	listPersonId: number | null | undefined
): ImportedConversation[] =>
	listPersonId
		? (state.conversation.importedByListPersonId[listPersonId]?.items ?? NO_IMPORTED)
		: NO_IMPORTED;

/**
 * Whether what the viewer loads on this lead is shared with the team (it is not a private lead);
 * null until the history was read.
 */
export const selectImportedShared = (
	state: RootState,
	listPersonId: number | null | undefined
): boolean | null =>
	listPersonId ? (state.conversation.importedByListPersonId[listPersonId]?.shared ?? null) : null;

/** Whether the server left older imported messages of this lead out (a capped page). */
export const selectImportedTruncated = (
	state: RootState,
	listPersonId: number | null | undefined
): boolean =>
	!!listPersonId && !!state.conversation.importedByListPersonId[listPersonId]?.truncated;

/** The last import answer for this list person, or null when it was never asked here. */
export const selectImportResult = (
	state: RootState,
	listPersonId: number | null | undefined
): ConversationImportResult | null =>
	listPersonId ? (state.conversation.importResultByListPersonId[listPersonId] ?? null) : null;
